import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import Thumbnail from '@base/Thumbnail/Thumbnail';
import Link from '@base/Link/Link';
import Divider from '@base/Divider/Divider';
import H6 from '@base/H6/H6';
import { LinkPath, getLink } from '@models/BatchGroup';
import { Spacings, mediaQuery, Fonts } from '@styles/index';
import Button from '@base/Button/Button';
import usePlatformEventProps from '@hooks/usePlatformBatchEventProps';
import { PLATFORM_BATCH_CARD_CLICKED } from '@constants/segment';
import { segment } from '@actions/analytics';
import H5 from '@base/H5/H5';
import {
  getFontSizeForPrice,
  getIsBatchCompleted,
  getIsEnrollmentClosed,
  getLanguagesForCard,
  getSlashPriceMessage,
  getEntitySubType
} from '@cont/BatchGroup/utils';
import BatchGroupCardInfo from '@cont/BatchGroup/BatchGroupCardInfo';
import BatchGroupTags from '@cont/BatchGroup/BatchGroupTags';
import { cardWidth, cardType } from '@cont/BatchGroup/constants';
import BatchCardFooter from '@cont/BatchGroup/BatchGroupCard/BatchCardFooter';
import { useMemo } from 'react';
import { Container } from '../CourseCard';

const CourseThumbnail = styled(Thumbnail)`
  border-top-left-radius: ${Spacings.SPACING_2B};
  border-top-right-radius: ${Spacings.SPACING_2B};
  ${mediaQuery.uptoTablet} {
    width: 100%;
    height: 190px;
  }
`;

const CardContainer = styled(Container)`
  position: relative;
  width: ${({ $cardType = cardType.MEDIUM }) => cardWidth[$cardType].web};
  margin-bottom: ${Spacings.SPACING_8B};
  background: var(--color-base-1);
  padding-bottom: ${Spacings.SPACING_0B};
  display: flex;
  flex-direction: column;
  .batch-thumbnail {
    width: 100%;
    height: 190px;
  }
  ${mediaQuery.uptoMobile} {
    width: ${({ $cardType = cardType.SMALL }) => cardWidth[$cardType].mobile};
    margin-bottom: ${Spacings.SPACING_0B};
    padding-bottom: ${Spacings.SPACING_2B};
    .batch-thumbnail {
      width: 100%;
      height: 150px;
    }
  }
`;

const Content = styled.div`
  padding: ${Spacings.SPACING_4B};
  ${mediaQuery.uptoTablet} {
    margin-bottom: 0;
  }
`;

const CourseTitle = styled(H6)`
  margin-top: ${Spacings.SPACING_1B};
  margin-bottom: ${Spacings.SPACING_4B};
  color: var(--color-text-primary);
  font-size: ${({ $isFeedCard }) => ($isFeedCard ? Fonts.LARGE : Fonts.SMALL)};
  ${mediaQuery.uptoTablet} {
    font-size: ${Fonts.LARGE};
  }
`;

const StyledDivider = styled(Divider)`
  margin: ${Spacings.SPACING_0B} auto ${Spacings.SPACING_3B} auto;
  width: 100%;
`;

const CardFooter = styled.div`
  width: 100%;
  margin-top: auto;
  padding: ${Spacings.SPACING_4B};
`;

const PriceText = styled(H6)`
  font-weight: ${Fonts.REGULAR};
  font-size: ${Spacings.SPACING_3B};
  color: ${({ $color }) => $color};
`;
const SlashPriceMessage = styled(PriceText)``;

const PriceCardSection = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CoursePriceAndTime = styled.div``;

const ViewDetails = styled(Button)`
  background-color: ${({ $secondary }) => {
    return $secondary ? 'unset' : 'var(--color-base-fill)';
  }};
  color: ${({ $secondary }) => {
    return $secondary ? 'unset' : 'var(--color-text-tertiary)';
  }};
  width: ${({ $isFeedCard }) => {
    return $isFeedCard ? '100px' : '120px';
  }};
  height: 36px;
  min-height: 36px;
  font-size: ${Fonts.ULTRA_SMALL};
  font-weight: ${Fonts.HEAVY};
  &:active {
    background-color: ${({ $secondary }) => {
      return $secondary ? 'unset' : 'var(--color-i-black)';
    }};
  }
`;
const PriceValue = styled(H5)`
  margin-left: ${({ $isSlashPriceAvailable }) => {
    return $isSlashPriceAvailable ? Spacings.SPACING_2B : Spacings.SPACING_0B;
  }};
  font-size: ${({ $fontSize = Fonts.LARGE }) => $fontSize};
  color: ${({ $color }) => $color};
`;

const SlashPrice = styled(PriceValue)`
  text-decoration-line: line-through;
  color: var(--color-text-secondary);
  font-weight: ${Fonts.LIGHT};
  font-size: ${({ $fontSize }) => $fontSize};
`;
const PriceWrapper = styled.div`
  display: flex;
`;
const StyledLink = styled(Link)`
  display: flex;
  flex-direction: column;
  height: 100%;
`;
const BatchInfoWrapper = styled.div`
  opacity: ${({ $hasOpacity }) => {
    return $hasOpacity ? 0.7 : 1;
  }};
`;
const PlatformBatchCard = ({
  gridColumn,
  className,
  batch,
  orientation,
  dataAnalytics,
  isFeedCard,
  lastPrimarySourceSection,
  cardType: platformCardType
}) => {
  const {
    languages,
    isEnrolled,
    enrollmentEndDate,
    price,
    name,
    isDiscountAvailable,
    discountEndDate,
    listPrice,
    promoText,
    completedAt,
    classType,
    stateMetaInfo
  } = batch;

  const entitySubType = useMemo(
    () => getEntitySubType(batch?.classType),
    [batch?.classType]
  );

  const segmentData = usePlatformEventProps({
    batch,
    entityContentType: true,
    lastPrimarySourceSection,
    isBatch: true,
    isSlashedPriceType: true,
    extraProps: { price, entity_sub_type: entitySubType }
  });

  if (!batch) return null;
  const handleAnalytics = () => {
    segment.track(PLATFORM_BATCH_CARD_CLICKED, segmentData);
  };
  const thumbnailUrl = batch.thumbnail || batch.coverPhoto;
  const isBatchAccessEnded = batch?.isAccessEnded ?? false;
  const isBatchCompleted = getIsBatchCompleted(completedAt);
  const slashPriceMessage = getSlashPriceMessage(
    discountEndDate,
    promoText,
    isDiscountAvailable,
    isBatchCompleted
  );

  const fontSizeForPrice = getFontSizeForPrice(
    price,
    listPrice,
    isDiscountAvailable
  );
  const isEnrollmentClose = getIsEnrollmentClosed(enrollmentEndDate);
  const languagesForCard = getLanguagesForCard(languages);
  return (
    <CardContainer
      $cardType={platformCardType}
      className={className}
      gridColumn={gridColumn}
      orientation={orientation}
    >
      <StyledLink href={LinkPath} as={getLink(batch)} onClick={handleAnalytics}>
        <CourseThumbnail
          imgUrl={thumbnailUrl}
          orientation={orientation}
          data-analytics={`${dataAnalytics}Card`}
        />

        <Content $isFeedCard={isFeedCard}>
          <BatchGroupTags languages={languagesForCard} classType={classType} />
          <BatchInfoWrapper $hasOpacity={isBatchAccessEnded}>
            <CourseTitle lineClamp={2} as="h4" $isFeedCard={isFeedCard}>
              {name}
            </CourseTitle>
            <BatchGroupCardInfo showDifferentIconOnDarkMode batch={batch} />
          </BatchInfoWrapper>
        </Content>
        <CardFooter>
          <StyledDivider />
          {!isEnrolled && (
            <PriceCardSection>
              <CoursePriceAndTime>
                {slashPriceMessage ? (
                  <SlashPriceMessage>{slashPriceMessage}</SlashPriceMessage>
                ) : (
                  <PriceText
                    $color={
                      isEnrollmentClose
                        ? 'var(--color-text-secondary)'
                        : 'var(--color-text-primary)'
                    }
                  >
                    Price
                  </PriceText>
                )}
                <PriceWrapper>
                  {isDiscountAvailable && listPrice && (
                    <SlashPrice $fontSize={fontSizeForPrice}>
                      ₹{listPrice}
                    </SlashPrice>
                  )}
                  <PriceValue
                    $isSlashPriceAvailable={isDiscountAvailable && listPrice}
                    $fontSize={fontSizeForPrice}
                    $color={
                      isEnrollmentClose
                        ? 'var(--color-text-secondary)'
                        : 'var(--color-text-primary)'
                    }
                  >
                    ₹{price}
                  </PriceValue>
                </PriceWrapper>
              </CoursePriceAndTime>
              <ViewDetails
                $secondary={isEnrollmentClose}
                $isFeedCard={isFeedCard}
                variant={isEnrollmentClose ? 'hollow' : 'filled'}
                label="View details"
              />
            </PriceCardSection>
          )}
          <BatchCardFooter
            isFeedCard={isFeedCard}
            stateMetaInfo={stateMetaInfo}
          />
        </CardFooter>
      </StyledLink>
    </CardContainer>
  );
};

export default PlatformBatchCard;

PlatformBatchCard.propTypes = {
  gridColumn: PropTypes.string,
  className: PropTypes.string,
  orientation: PropTypes.oneOf(['horizontal', 'vertical']),
  batch: PropTypes.objectOf(PropTypes.any).isRequired,
  dataAnalytics: PropTypes.string,
  isFeedCard: PropTypes.bool,
  cardType: PropTypes.string
};

PlatformBatchCard.defaultProps = {
  gridColumn: 'span 6',
  className: '',
  orientation: 'vertical',
  dataAnalytics: '',
  isFeedCard: false,
  cardType: cardType.MEDIUM
};
